/**
 * Repository / IaC inventory scanner — V105-3-1 / ADR-0072 §1, D-105-6.
 *
 * PURE: files in, product-level inventory clues out. No I/O, no Cloudflare imports
 * (check-imports). The GitHub connector adapter (packages/cloudflare) reads the files;
 * the inventory service (infra-inventory-service.ts) persists the result.
 *
 * What it reads, and the clue each gives (all resolved through the reviewed provider
 * registry, connectors/providers.ts — exact aliases only, never a guess):
 *   - package manifests: package.json (npm), requirements*.txt / pyproject.toml (pypi), go.mod
 *     → `sdk` aliases (`npm:resend` → resend/email);
 *   - wrangler.toml / wrangler.json(c): binding tables (`[[d1_databases]]`, `[ai]`, `[[queues.producers]]`)
 *     → `wrangler_binding` aliases; the config itself → cloudflare/workers; `[vars]` keys and
 *     `wrangler secret put X` lines → `env_var` aliases;
 *   - Terraform (*.tf): `source = "ns/name"` → `iac_provider`, `resource "type"` → `iac_resource`;
 *   - source files: import specifiers → `sdk`; `env.X` / `process.env.X` / `os.environ` names →
 *     `env_var`; `https://host` literals → `domain` (low weight: a hostname in code may be a
 *     customer API a collector reads, so a person confirms);
 *   - CI config paths (`.github/workflows/…`) → `ci_config`;
 *   - other IaC (V108-3-1 / ADR-0086 §2): OpenTofu (*.tofu, as Terraform), CloudFormation
 *     (`Type: AWS::S3::Bucket` → `aws:s3:bucket`), Bicep (`'Microsoft.X/y@…'` → `azure:microsoft.x/y`)
 *     → `cloud_resource_type` aliases; Kubernetes / Helm YAML `image:` → `container_image`.
 * Test files, fixtures, build output and vendored code are skipped: they name vendors without using them.
 *
 * Scan modes (ADR-0086 §2, amends ADR-0025 / D-RG-01):
 *   - `manifest_iac` — package manifests, IaC and CI config only; NO source file is read.
 *     The default for every automatic run.
 *   - `full` — `manifest_iac` plus source files (SDK imports, env reads, host literals).
 *     Owner/admin opt-in per repository (recorded, revocable).
 * Lockfiles are in the D-RG-01 allowlist but no parser reads them, so the filter leaves them
 * out (they would only spend the 4000-file / 40 MiB budget).
 *
 * Output: one item per provider × product with every hit (file + line) and the
 * call sites (source-file hits) that V105-5-4 counts as code impact. A provider-level
 * clue (`<provider>/*`) is folded away when the same scan found products of that provider.
 */
import { getProvider } from '../connectors/providers.js';
import { productDisplayName, resolveProductClue } from '../connectors/product-clues.js';
export const INFRA_SCAN_PARSER_VERSION = 'infra-repo-scan@1';
export const INFRA_FUNCTIONS = [
    'primary_data_store', 'secondary_data_store', 'compute', 'inference', 'identity', 'messaging',
    'observability', 'ci_cd', 'edge_network', 'business_support', 'other',
];
const MAX_INSTANCES_PER_ITEM = 50;
const D1_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const instanceKey = (i) => `${i.resource_type}|${i.native_id}|${i.env_name ?? ''}`;
function pushInstances(into, add) {
    const seen = new Set(into.map(instanceKey));
    for (const i of add) {
        if (into.length >= MAX_INSTANCES_PER_ITEM)
            return;
        if (seen.has(instanceKey(i)))
            continue;
        seen.add(instanceKey(i));
        into.push(i);
    }
}
/** ADR-0086 §2. `manifest_iac` is the default for automatic runs; `full` is an owner/admin opt-in. */
export const INFRA_SCAN_MODES = ['manifest_iac', 'full'];
export function isInfraScanMode(v) {
    return typeof v === 'string' && INFRA_SCAN_MODES.includes(v);
}
const MAX_HITS_PER_ITEM = 50;
const MAX_CALL_SITES_PER_ITEM = 200;
const MAX_FILE_BYTES = 262_144;
// ---- file selection ------------------------------------------------------------------------------
const SKIP_DIR = /(^|\/)(node_modules|dist|build|out|coverage|vendor|\.wrangler|\.git|\.next|__tests__|__fixtures__|__mocks__|fixtures|test-support|test-results|e2e)\//i;
const TEST_FILE = /\.(test|spec|eval)\.[a-z]+$|\.d\.ts$/i;
const MANIFEST = /(^|\/)(package\.json|requirements[^/]*\.txt|pyproject\.toml|go\.mod|wrangler\.toml|wrangler\.jsonc?)$|\.tf$/i;
const SOURCE = /\.(ts|tsx|mts|cts|js|jsx|mjs|cjs|py|go)$/i;
const CI = /^(\.github\/workflows\/[^/]+\.ya?ml|\.gitlab-ci\.yml|bitbucket-pipelines\.yml)$/i;
/** OpenTofu, Bicep, CloudFormation and Kubernetes / Helm YAML (by conventional path; content decides the clues). */
const IAC = /\.(tofu|bicep)$|(^|\/)(cloudformation|cfn)\/[^/]+\.(ya?ml|json)$|\.(template|cfn)\.(ya?ml|json)$|(^|\/)(Chart\.ya?ml|values[^/]*\.ya?ml|kustomization\.ya?ml)$|(^|\/)(k8s|kubernetes|helm|charts|manifests)\/(.+\/)?[^/]+\.ya?ml$/i;
/**
 * What the adapter should read: a manifest / IaC / CI file, a source file, or nothing.
 * In `manifest_iac` mode a source file is never read (ADR-0086 §2). `full` is the default
 * here so the pure scanner's existing callers keep their behaviour; every automatic run and
 * the GitHub adapter pass the mode explicitly.
 */
export function infraScanPathKind(path, mode = 'full') {
    const p = path.replace(/\\/g, '/').replace(/^\.\//, '');
    if (CI.test(p))
        return 'ci';
    if (SKIP_DIR.test(`/${p}`) || TEST_FILE.test(p))
        return null;
    if (/(^|\/)package-lock\.json$|(^|\/)tsconfig[^/]*\.json$/i.test(p))
        return null;
    if (MANIFEST.test(p))
        return 'manifest';
    if (IAC.test(p))
        return 'iac';
    if (SOURCE.test(p))
        return mode === 'full' ? 'source' : null;
    return null;
}
// ---- helpers --------------------------------------------------------------------------------------
/** Suggested function from the product, else the provider's category (ADR-0072 §1: a person sets the real one). */
const PRODUCT_FUNCTION = {
    'cloudflare/d1': 'primary_data_store', 'cloudflare/r2': 'secondary_data_store', 'cloudflare/kv': 'secondary_data_store',
    'cloudflare/vectorize': 'secondary_data_store', 'cloudflare/durable-objects': 'compute', 'cloudflare/hyperdrive': 'compute',
    'cloudflare/workers': 'compute', 'cloudflare/workflows': 'compute', 'cloudflare/queues': 'messaging',
    'cloudflare/workers-ai': 'inference', 'cloudflare/ai-gateway': 'inference', 'cloudflare/browser-rendering': 'compute',
    'cloudflare/pages': 'edge_network', 'aws/s3': 'secondary_data_store', 'aws/rds-postgres': 'primary_data_store',
    'aws/aurora-postgres': 'primary_data_store', 'aws/ses': 'messaging', 'aws/sqs': 'messaging', 'aws/bedrock': 'inference',
    'gcp/cloud-sql': 'primary_data_store', 'gcp/alloydb': 'primary_data_store', 'gcp/cloud-storage': 'secondary_data_store',
    'gcp/vertex-ai': 'inference', 'azure/postgresql': 'primary_data_store', 'azure/openai': 'inference', 'azure/blob-storage': 'secondary_data_store',
};
const CATEGORY_FUNCTION = {
    model_provider: 'inference', ai_gateway: 'inference', inference_runtime: 'inference', vector_db: 'secondary_data_store',
    database: 'primary_data_store', idp: 'identity', email: 'messaging', communications: 'messaging', observability: 'observability',
    scm: 'ci_cd', payments: 'business_support', collaboration: 'business_support', ai_app: 'business_support',
    cloud: 'compute', platform: 'compute', ml_platform: 'compute', security: 'other',
};
export function suggestInfraFunction(providerKey, product) {
    return PRODUCT_FUNCTION[`${providerKey}/${product}`] ?? CATEGORY_FUNCTION[getProvider(providerKey)?.category ?? ''] ?? 'other';
}
/** Newline offsets per text (one file at a time is live, so a one-entry cache is enough). */
let NL_CACHE = null;
/** 1-based line of the character at `index`. */
function lineAt(text, index) {
    if (NL_CACHE?.text !== text) {
        const nl = [];
        for (let i = text.indexOf('\n'); i >= 0; i = text.indexOf('\n', i + 1))
            nl.push(i);
        NL_CACHE = { text, nl };
    }
    const nl = NL_CACHE.nl;
    let lo = 0;
    let hi = nl.length;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (nl[mid] < index)
            lo = mid + 1;
        else
            hi = mid;
    }
    return lo + 1;
}
function lineOf(text, needle) {
    const i = text.indexOf(needle);
    return i < 0 ? 1 : lineAt(text, i);
}
/** `@scope/name/sub` → `@scope/name`; `name/sub` → `name`; relative / builtin → null. */
export function npmPackageRoot(spec) {
    const s = spec.trim();
    if (!s || s.startsWith('.') || s.startsWith('/') || s.startsWith('node:') || s.startsWith('#') || s.includes(':'))
        return null;
    const parts = s.split('/');
    if (s.startsWith('@'))
        return parts.length >= 2 ? `${parts[0]}/${parts[1]}`.toLowerCase() : null;
    return parts[0].toLowerCase();
}
/** Strip line and block comments outside strings, and trailing commas (for wrangler.jsonc). */
function stripJsonComments(text) {
    let out = '';
    let inStr = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inStr) {
            out += c;
            if (c === '\\') {
                out += text[i + 1] ?? '';
                i++;
            }
            else if (c === '"')
                inStr = false;
            continue;
        }
        if (c === '"') {
            inStr = true;
            out += c;
            continue;
        }
        if (c === '/' && text[i + 1] === '/') {
            while (i < text.length && text[i] !== '\n')
                i++;
            out += '\n';
            continue;
        }
        if (c === '/' && text[i + 1] === '*') {
            const e = text.indexOf('*/', i + 2);
            i = e < 0 ? text.length : e + 1;
            continue;
        }
        out += c;
    }
    return out.replace(/,(\s*[}\]])/g, '$1');
}
const WRANGLER_NON_BINDINGS = new Set(['vars', 'triggers', 'migrations', 'alias', 'build', 'observability', 'placement', 'limits', 'site', 'dev', 'env', 'unsafe', 'define', 'rules', 'tail_consumers', 'route', 'routes']);
const ENV_NAME = /^[A-Z][A-Z0-9_]{2,80}$/;
function parsePackageJson(f, out) {
    let j;
    try {
        j = JSON.parse(f.content);
    }
    catch {
        return;
    }
    for (const [section, weight] of [['dependencies', 1], ['optionalDependencies', 1], ['peerDependencies', 0.8], ['devDependencies', 0.5]]) {
        const deps = j[section];
        if (!deps || typeof deps !== 'object' || Array.isArray(deps))
            continue;
        for (const name of Object.keys(deps)) {
            const root = npmPackageRoot(name);
            if (!root)
                continue;
            out.push({ kind: 'sdk', aliasKind: 'sdk', value: `npm:${root}`, path: f.path, line: lineOf(f.content, `"${name}"`), weight, source: false });
        }
    }
}
function parseRequirements(f, out) {
    f.content.split('\n').forEach((raw, i) => {
        const m = /^\s*([A-Za-z0-9][A-Za-z0-9._-]*)/.exec(raw.replace(/#.*/, ''));
        if (m && !raw.trim().startsWith('-'))
            out.push({ kind: 'sdk', aliasKind: 'sdk', value: `pypi:${m[1].toLowerCase().replace(/_/g, '-')}`, path: f.path, line: i + 1, weight: 1, source: false });
    });
}
function parsePyproject(f, out) {
    let section = '';
    let inDeps = false;
    f.content.split('\n').forEach((raw, i) => {
        const line = raw.replace(/#.*/, '');
        const h = /^\s*\[([^\]]+)\]\s*$/.exec(line);
        if (h) {
            section = h[1].trim();
            inDeps = false;
            return;
        }
        if (/^\s*dependencies\s*=\s*\[/.test(line))
            inDeps = true;
        const names = [];
        if (inDeps)
            for (const m of line.matchAll(/["']([A-Za-z0-9][A-Za-z0-9._-]*)/g))
                names.push(m[1]);
        if (inDeps && line.includes(']'))
            inDeps = false;
        if (/^tool\.poetry\.(dev-)?dependencies$/.test(section)) {
            const m = /^\s*([A-Za-z0-9][A-Za-z0-9._-]*)\s*=/.exec(line);
            if (m && m[1].toLowerCase() !== 'python')
                names.push(m[1]);
        }
        for (const n of names)
            out.push({ kind: 'sdk', aliasKind: 'sdk', value: `pypi:${n.toLowerCase().replace(/_/g, '-')}`, path: f.path, line: i + 1, weight: 1, source: false });
    });
}
function parseGoMod(f, out) {
    f.content.split('\n').forEach((raw, i) => {
        const m = /^\s*(?:require\s+)?([a-z0-9.-]+\.[a-z]{2,}\/[^\s]+)\s+v\d/.exec(raw);
        if (m)
            out.push({ kind: 'sdk', aliasKind: 'sdk', value: `golang:${m[1].toLowerCase()}`, path: f.path, line: i + 1, weight: 1, source: false });
    });
}
function bindingClue(table, f, line, out) {
    const seg = table.replace(/^env\.[^.]+\./, '').split('.')[0].replace(/"/g, '').trim().toLowerCase();
    if (!seg || WRANGLER_NON_BINDINGS.has(seg))
        return;
    out.push({ kind: 'wrangler_binding', aliasKind: 'wrangler_binding', value: seg, path: f.path, line, weight: 1, source: false });
}
function envClue(name, f, line, out, source, weight = 1) {
    if (ENV_NAME.test(name))
        out.push({ kind: 'env_var', aliasKind: 'env_var', value: name, path: f.path, line, weight, source });
}
function parseWranglerToml(f, out, inst = []) {
    out.push({ kind: 'wrangler_config', aliasKind: 'wrangler_binding', value: 'services', path: f.path, line: 1, weight: 1, source: false });
    let section = '';
    // V109-2-1: the open `[[d1_databases]]` block (its env and the id / name seen so far).
    let d1 = null;
    const closeD1 = () => {
        if (d1?.id && D1_UUID.test(d1.id))
            inst.push({ resource_type: 'cloudflare.d1_database', native_id: d1.id.toLowerCase(), name: d1.name, env_name: d1.env, path: f.path, line: d1.line });
        d1 = null;
    };
    f.content.split('\n').forEach((raw, i) => {
        const secret = /wrangler\s+secret\s+put\s+([A-Z][A-Z0-9_]+)/.exec(raw);
        if (secret)
            envClue(secret[1], f, i + 1, out, false);
        const line = raw.replace(/#.*/, '');
        const h = /^\s*\[\[?\s*([A-Za-z0-9_.\-"]+)\s*\]\]?\s*$/.exec(line);
        if (h) {
            closeD1();
            section = h[1];
            bindingClue(section, f, i + 1, out);
            const m = /^(?:env\.("?)([A-Za-z0-9_-]+)\1\.)?d1_databases$/.exec(section);
            if (m)
                d1 = { env: m[2] ?? null, id: null, name: null, line: i + 1 };
            return;
        }
        if (d1) {
            const kv = /^\s*(database_id|database_name)\s*=\s*"([^"]{1,200})"/.exec(line);
            if (kv && kv[1] === 'database_id')
                d1.id = kv[2].trim();
            if (kv && kv[1] === 'database_name')
                d1.name = kv[2].trim();
        }
        if (/(^|\.)vars$/.test(section)) {
            const k = /^\s*([A-Z][A-Z0-9_]+)\s*=/.exec(line);
            if (k)
                envClue(k[1], f, i + 1, out, false);
        }
    });
    closeD1();
}
function parseWranglerJson(f, out, inst = []) {
    out.push({ kind: 'wrangler_config', aliasKind: 'wrangler_binding', value: 'services', path: f.path, line: 1, weight: 1, source: false });
    let j;
    try {
        j = JSON.parse(stripJsonComments(f.content));
    }
    catch {
        return;
    }
    const visit = (o, envName) => {
        for (const [k, v] of Object.entries(o)) {
            if (k === 'vars' && v && typeof v === 'object') {
                for (const name of Object.keys(v))
                    envClue(name, f, lineOf(f.content, `"${name}"`), out, false);
                continue;
            }
            if (k === 'env' && v && typeof v === 'object') {
                for (const [name, e] of Object.entries(v))
                    if (e && typeof e === 'object')
                        visit(e, name);
                continue;
            }
            if (k === 'd1_databases' && Array.isArray(v)) {
                for (const d of v) {
                    const id = typeof d?.database_id === 'string' ? d.database_id.trim() : '';
                    if (!D1_UUID.test(id))
                        continue;
                    inst.push({ resource_type: 'cloudflare.d1_database', native_id: id.toLowerCase(), name: typeof d.database_name === 'string' ? d.database_name.slice(0, 200) : null,
                        env_name: envName, path: f.path, line: lineOf(f.content, id) });
                }
            }
            if (v && typeof v === 'object')
                bindingClue(k, f, lineOf(f.content, `"${k}"`), out);
        }
    };
    visit(j, null);
}
function parseTerraform(f, out) {
    f.content.split('\n').forEach((raw, i) => {
        const line = raw.replace(/(#|\/\/).*/, '');
        const src = /^\s*source\s*=\s*"([a-z0-9-]+\/[a-z0-9_-]+)"/i.exec(line);
        if (src)
            out.push({ kind: 'iac_provider', aliasKind: 'iac_provider', value: src[1].toLowerCase(), path: f.path, line: i + 1, weight: 1, source: false });
        const res = /^\s*(?:resource|data)\s+"([a-z0-9_]+)"/i.exec(line);
        if (res)
            out.push({ kind: 'iac_resource', aliasKind: 'iac_resource', value: res[1].toLowerCase(), path: f.path, line: i + 1, weight: 1, source: false });
    });
}
/** CloudFormation `Type: AWS::S3::Bucket`, Bicep `'Microsoft.CognitiveServices/accounts@…'`, k8s `image:`. */
function parseIacText(f, out) {
    const lower = f.path.toLowerCase();
    if (lower.endsWith('.tofu')) {
        parseTerraform(f, out);
        return;
    }
    f.content.split('\n').forEach((raw, i) => {
        const line = raw.replace(/(^|\s)(#|\/\/).*/, '');
        const cfn = /["']?Type["']?\s*:\s*["']?(AWS::[A-Za-z0-9]+::[A-Za-z0-9]+)/.exec(line);
        if (cfn)
            out.push({ kind: 'iac_resource', aliasKind: 'cloud_resource_type', value: `aws:${cfn[1].slice(5).replace(/::/g, ':').toLowerCase()}`, path: f.path, line: i + 1, weight: 1, source: false });
        const bicep = /^\s*resource\s+\w+\s+'(Microsoft\.[A-Za-z]+\/[A-Za-z/]+)@/.exec(line);
        if (bicep)
            out.push({ kind: 'iac_resource', aliasKind: 'cloud_resource_type', value: `azure:${bicep[1].toLowerCase()}`, path: f.path, line: i + 1, weight: 1, source: false });
        const image = /^\s*-?\s*image\s*:\s*["']?([a-z0-9][a-z0-9./_-]*)(?::[^\s"']*)?["']?\s*$/i.exec(line);
        if (image)
            out.push({ kind: 'iac_resource', aliasKind: 'container_image', value: image[1].toLowerCase(), path: f.path, line: i + 1, weight: 0.8, source: false });
    });
}
const IMPORT_RES = [
    /\b(?:import|export)\s[^'"`;]*?\bfrom\s*['"]([^'"]+)['"]/g,
    /\bimport\s*\(\s*['"]([^'"]+)['"]\s*\)/g,
    /\bimport\s+['"]([^'"]+)['"]/g,
    /\brequire\s*\(\s*['"]([^'"]+)['"]\s*\)/g,
];
const ENV_RES = [
    /\b(?:process\.)?env\??\.([A-Z][A-Z0-9_]{2,80})\b/g,
    /\benv\[\s*['"]([A-Z][A-Z0-9_]{2,80})['"]\s*\]/g,
    /\bos\.(?:environ(?:\.get)?\s*[[(]|getenv\s*\()\s*['"]([A-Z][A-Z0-9_]{2,80})['"]/g,
    /\bos\.Getenv\(\s*"([A-Z][A-Z0-9_]{2,80})"\s*\)/g,
];
const HOST_RE = /\bhttps?:\/\/([a-z0-9-]+(?:\.[a-z0-9-]+)+)/gi;
const DOC_HOST = /^(docs|developers?|support|help|learn|status|blog|community|www\.docs)\./i;
function parseSource(f, out) {
    const text = f.content;
    const isJs = /\.(ts|tsx|mts|cts|js|jsx|mjs|cjs)$/i.test(f.path);
    if (isJs) {
        for (const re of IMPORT_RES) {
            for (const m of text.matchAll(re)) {
                const root = npmPackageRoot(m[1]);
                if (root)
                    out.push({ kind: 'sdk', aliasKind: 'sdk', value: `npm:${root}`, path: f.path, line: lineAt(text, m.index ?? 0), weight: 1, source: true });
            }
        }
    }
    for (const re of ENV_RES)
        for (const m of text.matchAll(re))
            envClue(m[1], f, lineAt(text, m.index ?? 0), out, true);
    for (const m of text.matchAll(HOST_RE)) {
        // A documentation link names a vendor without using it.
        if (DOC_HOST.test(m[1]))
            continue;
        out.push({ kind: 'domain', aliasKind: 'domain', value: m[1].toLowerCase(), path: f.path, line: lineAt(text, m.index ?? 0), weight: 0.5, source: true });
    }
}
// ---- scan --------------------------------------------------------------------------------------------
/** Scan repository files for third-party products. Deterministic: same files → same result. */
export function scanInfraRepo(files, opts = {}) {
    const mode = opts.mode ?? 'full';
    return finalizeInfraScan(infraScanPartial(files, mode), mode);
}
export const emptyInfraScanPartial = () => ({ scanned: 0, skipped: 0, hits_found: 0, refs: [] });
/**
 * The partial for these files (sorted by path here). With `prior`, the hits and call sites are trimmed
 * to the room `prior` leaves per ref, so a batch carries only what the merge will keep (step outputs stay small).
 */
export function infraScanPartial(files, mode, prior) {
    const raw = [];
    const inst = [];
    let scanned = 0;
    let skipped = 0;
    const sorted = [...files].sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
    for (const file of sorted) {
        const f = { path: file.path.replace(/\\/g, '/').replace(/^\.\//, ''), content: file.content.replace(/\r\n/g, '\n') };
        const kind = infraScanPathKind(f.path, mode);
        if (!kind || f.content.length > MAX_FILE_BYTES) {
            skipped++;
            continue;
        }
        scanned++;
        if (kind === 'ci') {
            raw.push({ kind: 'ci_config', aliasKind: 'ci_config', value: f.path.toLowerCase(), path: f.path, line: 1, weight: 1, source: false });
            continue;
        }
        const base = f.path.split('/').pop().toLowerCase();
        if (base === 'package.json')
            parsePackageJson(f, raw);
        else if (/^requirements[^/]*\.txt$/.test(base))
            parseRequirements(f, raw);
        else if (base === 'pyproject.toml')
            parsePyproject(f, raw);
        else if (base === 'go.mod')
            parseGoMod(f, raw);
        else if (base === 'wrangler.toml')
            parseWranglerToml(f, raw, inst);
        else if (/^wrangler\.jsonc?$/.test(base))
            parseWranglerJson(f, raw, inst);
        else if (base.endsWith('.tf'))
            parseTerraform(f, raw);
        else if (kind === 'iac')
            parseIacText(f, raw);
        else
            parseSource(f, raw);
    }
    const room = new Map((prior?.refs ?? []).map((r) => [r.key, { hits: MAX_HITS_PER_ITEM - r.hits.length, sites: MAX_CALL_SITES_PER_ITEM - r.call_sites.length }]));
    const byRef = new Map();
    let hitsFound = 0;
    for (const c of raw) {
        const r = resolveProductClue(c.aliasKind, c.value);
        if (!r)
            continue;
        // The wrangler config itself always means Workers, whatever alias carried it.
        const ref = c.kind === 'wrangler_config' ? { provider_key: 'cloudflare', product_id: 'workers' } : r;
        const key = `${ref.provider_key}/${ref.product_id}`;
        const conf = Math.round(Math.min(1, (c.kind === 'wrangler_config' ? 0.95 : r.confidence) * c.weight) * 100) / 100;
        let a = byRef.get(key);
        if (!a) {
            const left = room.get(key);
            a = {
                r: { key, provider_key: ref.provider_key, product: ref.product_id, total: 0, kinds: [], clues: [], hits: [], call_sites: [] },
                clues: new Map(), kinds: new Set(), sites: new Map(),
                maxHits: Math.max(0, left?.hits ?? MAX_HITS_PER_ITEM), maxSites: Math.max(0, left?.sites ?? MAX_CALL_SITES_PER_ITEM),
            };
            byRef.set(key, a);
        }
        hitsFound++;
        a.r.total++;
        a.kinds.add(c.kind);
        const clueKey = `${c.kind}:${c.value}`;
        a.clues.set(clueKey, Math.max(a.clues.get(clueKey) ?? 0, conf));
        if (a.r.hits.length < a.maxHits)
            a.r.hits.push({ kind: c.kind, value: c.value, path: c.path, line: c.line, confidence: conf });
        if (c.source && a.sites.size < a.maxSites)
            a.sites.set(`${c.path}:${c.line}`, { path: c.path, line: c.line });
    }
    const refs = [...byRef.values()].map((a) => ({ ...a.r, kinds: [...a.kinds], clues: [...a.clues.entries()], call_sites: [...a.sites.values()] }));
    // V109-2-1: D1 database ids ride on the `cloudflare/d1` ref (the binding clue always created it).
    const d1 = refs.find((r) => r.key === 'cloudflare/d1');
    if (d1 && inst.length) {
        const into = [];
        pushInstances(into, inst);
        d1.instances = into;
    }
    return { scanned, skipped, hits_found: hitsFound, refs };
}
/** Merge two partials, `a` before `b` in path order (the same caps as one pass). */
export function mergeInfraScanPartials(a, b) {
    const byKey = new Map();
    for (const r of a.refs)
        byKey.set(r.key, { ...r, kinds: [...r.kinds], clues: [...r.clues], hits: [...r.hits], call_sites: [...r.call_sites] });
    for (const r of b.refs) {
        const cur = byKey.get(r.key);
        if (!cur) {
            byKey.set(r.key, { ...r, kinds: [...r.kinds], clues: [...r.clues], hits: r.hits.slice(0, MAX_HITS_PER_ITEM), call_sites: r.call_sites.slice(0, MAX_CALL_SITES_PER_ITEM) });
            continue;
        }
        cur.total += r.total;
        cur.kinds = [...new Set([...cur.kinds, ...r.kinds])];
        const clues = new Map(cur.clues);
        for (const [k, v] of r.clues)
            clues.set(k, Math.max(clues.get(k) ?? 0, v));
        cur.clues = [...clues.entries()];
        cur.hits = [...cur.hits, ...r.hits].slice(0, MAX_HITS_PER_ITEM);
        const sites = new Map(cur.call_sites.map((s) => [`${s.path}:${s.line}`, s]));
        for (const s of r.call_sites) {
            const k = `${s.path}:${s.line}`;
            if (sites.has(k) || sites.size < MAX_CALL_SITES_PER_ITEM)
                sites.set(k, s);
        }
        cur.call_sites = [...sites.values()];
        if (r.instances?.length) {
            const into = [...(cur.instances ?? [])];
            pushInstances(into, r.instances);
            cur.instances = into;
        }
    }
    return { scanned: a.scanned + b.scanned, skipped: a.skipped + b.skipped, hits_found: a.hits_found + b.hits_found, refs: [...byKey.values()] };
}
/** The `InfraScanResult` of a (merged) partial. */
export function finalizeInfraScan(p, mode) {
    // Fold a provider-level item away when the scan also found that provider's products.
    const withProducts = new Set(p.refs.filter((a) => a.product !== '*').map((a) => a.provider_key));
    const items = [];
    for (const a of p.refs) {
        if (a.product === '*' && withProducts.has(a.provider_key))
            continue;
        const confidence = Math.min(0.99, Math.round((1 - a.clues.reduce((acc, [, c]) => acc * (1 - c), 1)) * 100) / 100);
        items.push({
            ref: a.key, provider_key: a.provider_key, product: a.product,
            name: productDisplayName({ provider_key: a.provider_key, product_id: a.product }),
            suggested_function: suggestInfraFunction(a.provider_key, a.product),
            confidence, clue_kinds: [...a.kinds].sort(), hits: a.hits, call_sites: a.call_sites,
            ...(a.instances?.length ? { instances: a.instances } : {}),
        });
    }
    items.sort((x, y) => (x.ref < y.ref ? -1 : x.ref > y.ref ? 1 : 0));
    return { parser_version: INFRA_SCAN_PARSER_VERSION, mode, files_scanned: p.scanned, files_skipped: p.skipped, items, hits_found: p.hits_found };
}
/**
 * A directory the scan never needs to list: every path under it is refused by `infraScanPathKind`
 * (build output, vendored code, tests, fixtures). CI config lives only under the root `.github/workflows/`.
 */
export function infraScanDirPruned(dir) {
    const d = dir.replace(/\\/g, '/').replace(/^\.\//, '').replace(/\/+$/, '');
    if (d === '.github' || d === '.github/workflows')
        return false;
    return SKIP_DIR.test(`/${d}/`);
}
//# sourceMappingURL=infra-repo-scanner.js.map